import { Component, ElementRef, NgZone, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import Decimal from 'decimal.js';
import { forkJoin, Subscription, Observable, combineLatest } from 'rxjs';
import { debounceTime, map, take, takeUntil } from 'rxjs/operators';
import { CurrencyDigitShortcutPipeInput } from 'src/app/pipes/pipes';
import { TranslatedCurrencyDigitInputs, TranslatedCurrencyYearInputs } from 'src/app/pipes/translated-currency-input-keys';
import { LoggedUser } from 'src/app/services/authentication/authentication';
import { ConfigService } from 'src/app/services/json-config.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { ApiAvailableCash, ApiExpectedStrategy, ApiUpdateStrategy, EntryFeeCalculationWithAllRatiosOut, FrameworkContractOut, ManagementTypesEnum, ModelPortfolioInstruments, ModelStrategyOrPortfolioInstrumentOut, PortfolioOut, ClientPortfolioEntryFeeCalcInput, StrategyInitData, StrategyLimitOut, InstrumentDetailFullOut, InvestmentFrequency, ApiEfektikaUpdateStrategy, ApiModelPortfolioChartOut } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { StrategyFormInstrument, StrategyInstrumentsFormService } from 'src/app/services/strategy-instruments-form.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { UserService } from 'src/app/services/user/user.service';
import { CurrencyCodesEnum, DevelopmentChartData, FormInstrument, InstrumentCategories, InstrumentChartCardInput } from 'src/app/shared/charts/charts';
import { availableInvestmentPeriods, CUSTOM_STRATEGY_GLOBAL_INDEX, EfektikaInstrumentWeights, maxInvestmentLength, newPortfolioConfig } from 'src/app/shared/constants';
import { adjustDevelopmentTimeLabels, AvailableCurrencySymbols, getCurrencyFromCode, getInstrumentsWeightSum, isInputError, parseTimestamps } from 'src/app/shared/functions';
import { investmentAlreadyExistError } from 'src/app/shared/functions-angular';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { distinctUntilChanged } from 'rxjs/operators';
import { ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { SignalsService } from 'src/app/services/signals.service';
import { StrategyOriginalValues } from './new-strategy';
import { TextInputComponent } from 'src/app/shared/text-input/text-input.component';
import { IosWorkaroundService } from 'src/app/services/ios-workaround.service';
import { ViewportScroller } from '@angular/common';
import tippy, { Instance, Props } from 'tippy.js';
import * as Sentry from '@sentry/browser';
import { EntryFeeCalcService } from 'src/app/shared/entry-fee-calc/entry-fee-calc.service';
import { trigger } from '@angular/animations';
import { fadeInOutAnimation } from 'src/app/shared/animations-angular';
import { RadiosKeyValue } from 'src/app/shared/radio-input-group/radio-input-group';
import isEqual from 'lodash/isEqual'
import { Location } from '@angular/common';
import { PlusMinusInputComponent } from 'src/app/shared/plus-minus-input/plus-minus-input.component';
import { RadioInputGroupComponent } from 'src/app/shared/radio-input-group/radio-input-group.component';

interface InitEditDataInput {
  ownedStrategies: ExpectedOwnedStrategy[],
  instruments: PortfolioOut,
  instrumentsChanged: boolean,
};

interface SavedStrategyInvestParams {
  initialDeposit: number;
  regularDeposit: number;
  weeksBetweenInvestments: RadiosKeyValue;
  totalYearsToInvest: number;
}

@Component({
  templateUrl: './new-strategy.component.html',
  styleUrls: ['./new-strategy.component.scss'],
  providers: [EntryFeeCalcService],
  animations: [
      trigger('fadeInOut', fadeInOutAnimation),
  ],
  standalone: false
})
export class NewStrategyComponent implements OnInit, OnDestroy {
  public strategyDescriptionVisible = false;
  public maxInvestmentLength = maxInvestmentLength;
  public modelStrategy = false;
  public strategyLimits?: StrategyLimitOut;
  public hideDipSwitch = false;
  private dipConfirmForm!: UntypedFormGroup;
  private dipConfirmFormSubscription?: Subscription;
  private creationConfirmSubscription?: Subscription;
  public editStrategyOrigin: string | null = null;
  public rebalancingSwitchDisabled = false; // used with specific toltip error text
  public confirmedFeesChange = false;
  public entryFeeToDisplay: EntryFeeCalculationWithAllRatiosOut | null = null;
  private formSubscription?: Subscription;
  public editingEfektika = false;

  // If user PORTFOLIO_MANAGEMENT and has some strategies, the guard takes a while
  // to allow the route for instruments edit, need to show instruments edit is loading
  public loadingForInstrumentsEdit = false;

  @ViewChild('strategyChecksum') public strategyChecksum!: ElementRef;
  @ViewChild('nameInputComponent') public nameInputComponent!: TextInputComponent;
  @ViewChild('tooltipDisabledRebalancingByEstateEl') public tooltipDisabledRebalancingByEstateContent?: ElementRef;
  private tooltipDisabledRebalancingByEstateHoverable?: ElementRef;
  private entryFeeToDisplaySub: Subscription | null = null;

  @ViewChild('tooltipDisabledRebalancingByEstateHoverable') public set content(content: ElementRef) {
     if(content) { // initially setter gets called with undefined
        this.tooltipDisabledRebalancingByEstateHoverable = content;
        this.initRebalancingTooltip();
     }
  }

  private efektikaNonEditElements?: QueryList<PlusMinusInputComponent | RadioInputGroupComponent>;
  @ViewChildren('efektikaNonEditEl') public set efektikaTooltips(content: QueryList<PlusMinusInputComponent>) {
     if(content) { // initially setter gets called with undefined
        this.efektikaNonEditElements = content;
        this.initEfektikaTooltip();
     }
  }

  @ViewChild('tooltipEfektikaEl') public tooltipEfektikaEl?: ElementRef;
  private efektikaTooltipInstances: Instance<Props>[] | null = null;

  public checksumValidMessages: Record<string, boolean> = {
    checksumNot100: false,
    instrumentsWith0: false
  }

  public get actualInstrumentsSum(): number {
    let formSum = 0;

    if (this.instrumentsForm) {
      for (const instrumentControl of this.instrumentsForm.controls) {
        formSum += instrumentControl.value.value;
      }

      // reset validity msgs
      if (this.checksumValidMessages['checksumNot100'] && formSum === 100) {
        this.checksumValidMessages['checksumNot100'] = false;
      }

      if (this.checksumValidMessages['instrumentsWith0'] && this.instrumentsForm.controls.every(instrumentControl => instrumentControl.value.value !== 0)) {
        this.checksumValidMessages['instrumentsWith0'] = false;
      }
    }

    return formSum;
  }

  public minCashThreshold = NaN;
  public minInstrumentThreshold = NaN;

  public editId: number | null = null;
  public editStrategyName: string | null = null;

  public savedInvestmentParams: SavedStrategyInvestParams | null = null;

  public get bothInvestmentsZero(): boolean {
    return Decimal.sum(this.modelChartForm.value['oneTimeDeposit'], this.modelChartForm.value['regularDeposit']).isZero();
  }

  public dipAvailable = false;

  private get isInstrumentWithRebalancedMonthlySelected(): boolean {
    return this.instrumentsService.selectedInstruments?.value.some((ins: InstrumentDetailFullOut) => ins.rebalancedMonthly);
  }

  public preloaders = {
    modelStrategy: true,
    instruments: true,
    postingStrategy: false,
    editDataFetching: false,
    editStrategy: false,
    modelChart: false,
  }

  public apiErrors = {
    modelChart: false,
    insufficentModelChartWeights: false,
  }

  public developmentChartLabels: number[] = [];
  public developmentChartData: DevelopmentChartData = {
    optimisticScenario: [],
    probableScenario: [],
    pessimisticScenario: [],
    deposits: [],
  };

  public modelStrategyApiData: ModelStrategyOrPortfolioInstrumentOut | null = null;
  public EnvironmentKeys = EnvironmentKeys;

  public yearsShortcutsOptions: CurrencyDigitShortcutPipeInput = {
    units: TranslatedCurrencyYearInputs
  }

  public digitShortcutsOptions: CurrencyDigitShortcutPipeInput = {
    digitShortcuts: TranslatedCurrencyDigitInputs
  }

  public availableInvestmentPeriods = availableInvestmentPeriods;

  public instruments?: InstrumentDetailFullOut[];
  public strategyOriginalValues: StrategyOriginalValues = {
    rebalance: false, instrumentsWithWeights: []
  };
  public availableCash?: ApiAvailableCash[];

  public modelChartForm!: UntypedFormGroup;

  public availableContracts: FrameworkContractOut[] = [];

  public singleEfektikaInstrumentWeight = EfektikaInstrumentWeights.singleEfektikaInstrumentWeight;
  public efektikaCashMax = EfektikaInstrumentWeights.efektikaCashMax;

  public get instrumentChart(): InstrumentChartCardInput {
    const chartData = this.instrumentsService.selectedInstruments?.value.map((instrument: StrategyFormInstrument) =>
    {
       return {
        weight: parseFloat((instrument.value / 100).toString()), // 1 = 100%
        instrument: this.getCorrectInstrument(instrument.rawInstrument ?? instrument),
      }
    }) ??  [];
    return {
      chartData,
      id: 0,
    };
  }

  private getCorrectInstrument(instrument: StrategyFormInstrument | FormInstrument | InstrumentDetailFullOut) {
    const validatedInstrument = 'rawInstrument' in instrument ? instrument.rawInstrument : instrument

    // should NOT hapen todo - probably delete for V1
    if(!validatedInstrument.regionalOrientations && !validatedInstrument.cash) {
      // if regionalOrientations missing at model strategy try to pair it with allInstruments to get it
      const instrumentsRegionFromGlobalObject = this.instruments?.find(inst => inst.instrumentId);
      validatedInstrument.regionalOrientations = instrumentsRegionFromGlobalObject?.regionalOrientations ?? [];
    }

    return validatedInstrument;
  }

  private get instrumentsForm(): UntypedFormArray | null {
    return this.modelChartForm?.get('instruments') as UntypedFormArray ?? null;
  }

  private get loggedUser(): LoggedUser {
    if(!this.userService.loggedUser || this.userService.loggedUser === null) {
      this.router.navigate(['/login']);
    }

    return this.userService.loggedUser!;
  }

  private get initialContract(): FrameworkContractOut {
    return this.availableContracts.find(contract => !contract.dip) ?? this.availableContracts[0];
  }

  public constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private instrumentsService: StrategyInstrumentsFormService,
    private restApiService: RestApiService,
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
    public userService: UserService,
    public modalService: ModalService,
    private router: Router,
    public configService: ConfigService,
    private signalsService: SignalsService,
    private iosWorkaroundService: IosWorkaroundService,
    private viewportScroller: ViewportScroller,
    private zone: NgZone,
    public entryFeeCalcService: EntryFeeCalcService,
    private location: Location,
  ) { }

  public ngOnInit(): void {
    if(this.iosWorkaroundService.isIosBrowser) {
      // workaround; ios/Mac safari is not scrolling correctly to top after edit init
      this.scrollTopAfterDelay();
    }

    const neccessaryInitReqests = [
      this.route.paramMap.pipe(take(1)),
      this.restApiService.getInstruments(),
      this.restApiService.getAvailableCash(),
      this.restApiService.getStrategyLimits(),
    ];

    forkJoin(neccessaryInitReqests).pipe(
      take(1),
      map(
        ([ params, availableInstruments, availableCash, limits ]) =>
        ({ params, availableInstruments, availableCash, limits } as StrategyInitData)
      ),
    ).subscribe({
        error: this.handleInitError.bind(this),
        next: this.handleInitData.bind(this),
    });

    this.availableContracts.push(...this.userService.loggedUser!.validContracts.filter(contract => !contract.inLiquidation));
    this.hideDipSwitch = this.availableContracts.length === 1 && !this.availableContracts.find(contract => contract.dip);
    this.dipAvailable = this.availableContracts.some(contract => contract.dip);
    this.entryFeeToDisplaySub = this.entryFeeCalcService.$entryFeeCalc
    .subscribe(data => this.entryFeeToDisplay = data);
  }

  private initEfektikaTooltip(): void {
    if (!this.editingEfektika || !this.efektikaNonEditElements?.toArray()?.length || !this.tooltipEfektikaEl) {
      return;
    }

    if(this.efektikaTooltipInstances) {
      this.efektikaTooltipInstances.forEach(i => i.destroy());
      this.efektikaTooltipInstances = null;
    }

    if (this.tooltipEfektikaEl) {
      this.efektikaTooltipInstances = tippy(
        this.efektikaNonEditElements.toArray().map(ref => ref.elementRef?.nativeElement),
        {
          content: this.tooltipEfektikaEl.nativeElement.innerHTML,
          allowHTML: true,
          placement: 'auto',
        }
      );
    }
  }

  private scrollTopAfterDelay(): void {
    setTimeout(() => {
      this.zone.run(() => {
        this.viewportScroller.scrollToPosition([0, 0]);
      });
    }, 150);
  }

  private handleInitError(error: unknown): void {
    this.preloaders.modelStrategy = false;
    this.preloaders.instruments = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  private handleInitData(data: StrategyInitData): void {
    const skipServiceInstrumentsReset = data.params
      .get('skipServiceInstrumentsReset');

    if (!skipServiceInstrumentsReset) {
      // important to reset to prevent from previous unsaved edit instruments being used in wrong strategy
      this.instrumentsService.selectedInstruments = null;
    }

    this.instruments = data.availableInstruments;
    this.availableCash = data.availableCash;
    this.preloaders.instruments = false;

    this.strategyLimits = data.limits;
    this.minInstrumentThreshold = data.limits.minInstrumentThreshold * 100;

    const editId = data.params.get('editStrategyId');
    this.editStrategyOrigin = data.params.get('origin');

    // New strategy after editing instruments
    if (!editId && data.params.get('skipDataInit') && this.instrumentsService.selectedInstruments?.length) {
      this.initFromService();
    // Edit strategy after editing instruments
    } else if(editId && this.instrumentsService.selectedInstruments?.length) {
      this.editId = parseInt(editId);
      this.initEdit(true);
    // Edit strategy without editing instruments yet
    } else if(editId && !this.instrumentsService.selectedInstruments?.length) {
      this.editId = parseInt(editId);
      this.initEdit(false);
    // New strategy without editing instruments yet
    } else {
      this.initNew(data.params.get('modelStrategyId'));
    }
  }

  private initEdit(instrumentsChanged: boolean): void {
    this.preloaders.editDataFetching = true;
    const neccessaryEditRequests: [
      Observable<ExpectedOwnedStrategy[]>,
      Observable<PortfolioOut>,
    ] = [
      this.restApiService.getPortfoliosAndStrategies(),
      this.restApiService.getInvestmentDetail({portfolioId: this.editId!}),
    ];

    forkJoin(neccessaryEditRequests).pipe(
      take(1),
      map(([portfolios, instruments]: [ExpectedOwnedStrategy[], PortfolioOut]) =>
        ({ ownedStrategies: portfolios, instruments, instrumentsChanged } as InitEditDataInput)
      ),
    ).subscribe({
        error: this.initEditError.bind(this),
        next: this.initEditData.bind(this),
    });
  }

  private getInstrumentsControlsArr(
    portfolioInstruments: ModelPortfolioInstruments[]): AbstractControl[] {
      const instruments: AbstractControl[] = [];
      for (const instrument of portfolioInstruments) {
        const instrumentWeightInpercent = parseInt((instrument.weight * 100).toString());
        instruments.push(this.createInstrumentControl(instrument.instrument, instrumentWeightInpercent));
      }
      return instruments;
  }

  private initEditData(data: InitEditDataInput): void {
    const strategyData = data.ownedStrategies.find(investment => investment.portfolioId === this.editId);

    if(!strategyData) {
      this.initEditError('Edit strategy ID was not found in existing investments!');
    } else {
      this.editingEfektika = strategyData.efektika ?? false;
      this.strategyOriginalValues.name = strategyData.name;
      this.strategyOriginalValues.portfolioChangeAffectsFees = data.instruments.portfolioChangeAffectsFees;

      this.strategyOriginalValues.instrumentsWithWeights = data
          .instruments.modelPortfolioInstruments;
      if (
        strategyData.strategyRebalance !== null &&
        strategyData.strategyRebalance !== undefined
      ) {
        this.strategyOriginalValues.rebalance = strategyData.strategyRebalance;
      }
      const instruments: AbstractControl[] = this.getInstrumentsControlsArr(
        data.instruments.modelPortfolioInstruments
      );
      const originalInstruments: AbstractControl[] = this.getInstrumentsControlsArr(
        [...data.instruments.modelPortfolioInstruments]
      );

      this.addEfektikaInstruments(data.instruments.modelPortfolioInstruments);

      this.editStrategyName = strategyData.name;
      const depositRegularity = this.availableInvestmentPeriods.find(period => +period.value === strategyData.weeksBetweenInvestments);
      if (!depositRegularity)
        this.initEditError(
          'Edit strategy has invalid weeks between investments!',
        );

      this.modelChartForm = new UntypedFormGroup({
        name: new UntypedFormControl(strategyData.name, [Validators.required, Validators.minLength(3), Validators.maxLength(newPortfolioConfig.investmentNameMaxLength)]),
        oneTimeDeposit: new UntypedFormControl(strategyData.initialDeposit),
        regularDeposit: new UntypedFormControl(strategyData.regularDeposit),
        timeOfInvestment: new UntypedFormControl(strategyData.totalYearsToInvest),
        riskProfile: new UntypedFormControl(strategyData.riskScore),
        // todo - only edit name allowed in this version
        depositRegularity: new UntypedFormControl(depositRegularity),
        dip: new UntypedFormControl(strategyData.dip),
        rebalancing: new UntypedFormControl(this.strategyOriginalValues.rebalance),
        instruments: data.instrumentsChanged ? this.instrumentsService.selectedInstruments! : new UntypedFormArray(instruments),
      });
      this.savedInvestmentParams = {
        initialDeposit: strategyData.initialDeposit,
        regularDeposit: strategyData.regularDeposit,
        totalYearsToInvest: strategyData.totalYearsToInvest,
        weeksBetweenInvestments: depositRegularity ?? this.availableInvestmentPeriods[0]
      }

      this.availableInvestmentPeriods = this.availableInvestmentPeriods.map(period => {
        const periodMutable = {...period};
        if (strategyData.weeksBetweenInvestments < +period.value) {
          periodMutable.disabled = true;
        }
        return periodMutable;
      });

      if (!data.instrumentsChanged) {
        this.instrumentsService.selectedInstruments = this.instrumentsForm!;
        this.instrumentsService.strategyOriginalInstruments = new UntypedFormArray(originalInstruments);
      }

      this.initFormSub();
      this.updateCashLimit();
      this.handleEditRebalancingDisable();

      this.preloaders.modelStrategy = false;
      this.preloaders.editDataFetching = false;
    }
  }

  private addEfektikaInstruments(modelPortfolioInstruments: ModelPortfolioInstruments[]) {
    if (this.editingEfektika && this.instruments?.length) {
      this.instruments!.push(
        ...modelPortfolioInstruments
        .map(i => i.instrument)
        .filter(b => !this.instruments!.some(a => a.instrumentId === b.instrumentId) && !b.cash)
      )
    }
  }

  private handleEditRebalancingDisable(): void {
    const rebalancingControl = this.modelChartForm.controls['rebalancing'];

    if(rebalancingControl.value === false) {
      this.rebalancingSwitchDisabled = this.isInstrumentWithRebalancedMonthlySelected;
    }
  }

  private initEditError(error: unknown): void {
    if(isInputError(error, 404)) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.strategyEditNotFound',
        type: 'warning',
        component: ToastNotificationComponent
      });
    } else {
      this.loggerService.error(typeof error === 'function' ? error() : error);
      this.toastService.addNotification({
        message: 'toastNotifications.messages.strategyEditLoadError',
        type: 'warning',
        component: ToastNotificationComponent
      });
    }

    this.router.navigate(['/strategy/new']);
    this.preloaders.editDataFetching = false;
  }

  private initNew(modelId: string | null): void {
    // set monthly as default
    const monthlyDefaultRegularityIndex = this.availableInvestmentPeriods.findIndex(period => period.key === 'monthly');

    this.modelChartForm = new UntypedFormGroup({
      name: new UntypedFormControl('', [Validators.required, Validators.minLength(newPortfolioConfig.investmentNameMinLength), Validators.maxLength(newPortfolioConfig.investmentNameMaxLength)]),
      oneTimeDeposit: new UntypedFormControl(this.configService.getConfig(EnvironmentKeys.minimalInvest)),
      regularDeposit: new UntypedFormControl(this.configService.getConfig(EnvironmentKeys.minimalInvest)),
      timeOfInvestment: new UntypedFormControl(0),
      rebalancing: new UntypedFormControl(false),
      instruments: new UntypedFormArray([]),
      depositRegularity: new UntypedFormControl(this.availableInvestmentPeriods[monthlyDefaultRegularityIndex]),
      dip: new UntypedFormControl(this.initialContract),
    })

    if(modelId && parseInt(modelId) > 0) {
      // API model porfolio init
      this.modelStrategy = true;
      this.getModelStrategyData(modelId);
    } else {
      // user defined or static thematic portfolio card (id < 0)
      this.initClear(parseInt(modelId + '') === CUSTOM_STRATEGY_GLOBAL_INDEX.modelStrategyId);
    }

    this.instruments = this.instruments?.filter((i: InstrumentDetailFullOut) => this.loggedUser.instrumentCategories
    // Each instrument's instrument category must be present in allowed instrument categories and instrument must not be marked for removal
        .some(ic => ic === i.instrumentCategory) && !i.willBeRemoved) as InstrumentDetailFullOut[] ?? [];
  }

  private initFromService() {
    this.preloaders.modelStrategy = false;

    this.redistributeInstrumentsWeights();

    // set monthly as default
    const monthlyDefaultRegularityIndex = this.availableInvestmentPeriods.findIndex(period => period.key === 'monthly');

    //todo - patch form data to local storage - UP: already planned feature
    this.modelChartForm = new UntypedFormGroup({
      name: new UntypedFormControl('', [Validators.required, Validators.minLength(newPortfolioConfig.investmentNameMinLength), Validators.maxLength(newPortfolioConfig.investmentNameMaxLength)]),
      oneTimeDeposit: new UntypedFormControl(this.configService.getConfig(EnvironmentKeys.minimalInvest)),
      regularDeposit: new UntypedFormControl(this.configService.getConfig(EnvironmentKeys.minimalInvest)),
      timeOfInvestment: new UntypedFormControl(0),
      instruments: this.instrumentsService.selectedInstruments!,
      rebalancing: new UntypedFormControl(false),
      depositRegularity: new UntypedFormControl(this.availableInvestmentPeriods[monthlyDefaultRegularityIndex]),
      dip: new UntypedFormControl(this.initialContract),
    });
    this.initFormSub();

    this.rebalancingSwitchDisabled = this.isInstrumentWithRebalancedMonthlySelected;
  }

  private redistributeInstrumentsWeights(): void {
    this.updateCashLimit();
    let cashWeight = this.instrumentsService.selectedInstruments!.value.find((instrument: StrategyFormInstrument) => instrument.cash)!.value;

    if(cashWeight < this.minCashThreshold) {
      // notify user about forced cash update
      if(this.strategyLimits && this.minCashThreshold === (this.strategyLimits?.minCashRebalancedMonthlyThreshold * 100)) {
        this.toastService.addNotification({
          title: 'toastNotifications.headers.updated',
          message: 'toastNotifications.messages.forcedEstateCashUpdate',
          type: 'success',
          component: ToastNotificationComponent,
          extendTimeout: true,
          payload: {
            suffix: this.minCashThreshold + ' %.',
          }
        });
      }

      cashWeight = this.minCashThreshold;
    }

    const withoutCash = parseInt((100 - cashWeight).toString());
    const instrumentsCount = this.instrumentsService.selectedInstruments!.value.length - 1; // exclude cash
    const averageWeight = Math.floor(withoutCash / instrumentsCount);

    for (const instrument of this.instrumentsService.selectedInstruments!.controls) {
      if(instrument.value.cash) {
        instrument.patchValue({ value: cashWeight })
        continue;
      }

      instrument.patchValue({ value: averageWeight });
    }

    // This section redistributes the modulo between all instruments by incrementing 1 until we run out of percents
    const glancingPercent = withoutCash % instrumentsCount;
    if(glancingPercent) {
      const nonCashInstruments = this.instrumentsService.selectedInstruments!.value.filter((i: StrategyFormInstrument) => !i.cash)

      let counter = 0
      let idx = 0
      while (counter < glancingPercent) {
        nonCashInstruments[idx].value += 1
        counter++
        if (idx < nonCashInstruments.length) {
          idx++
        } else {
          idx = 0
        }
      }
    }
  }

  public toggleDescription(): void {
    this.strategyDescriptionVisible = !this.strategyDescriptionVisible;
  }

  private initClear(redirectToInstruments?: boolean): void {
    this.preloaders.modelStrategy = false;

    this.pushUserDefaultCash();

    this.instrumentsService.selectedInstruments = this.instrumentsForm!;

    this.rebalancingSwitchDisabled = this.isInstrumentWithRebalancedMonthlySelected;

    if(redirectToInstruments) {
      this.router.navigate(['/strategy/new/edit-instruments'], { replaceUrl: true });
    }

    this.initFormSub();
  }

  private pushUserDefaultCash() {
    const defaultCashInstrument = this.availableCash?.find(availableCash => availableCash.currencyCode === this.loggedUser.currencyCode) as InstrumentDetailFullOut;

    if (!defaultCashInstrument) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.cashInstrumentFail',
        type: 'warning',
        component: ToastNotificationComponent
      });
      throw new Error('Failed to find user\'s currency default Cash Instrument!');
    }

    defaultCashInstrument.instrumentCategory = InstrumentCategories.MONEY;

    this.updateCashLimit();

    this.instrumentsForm!.push(this.createInstrumentControl(defaultCashInstrument, this.minCashThreshold));
  }


  private updateCashLimit(): void {
    if(!this.strategyLimits) {
      console.error('Strategy limits not found, fetch probably not called.')
    }
    if(this.isInstrumentWithRebalancedMonthlySelected && this.strategyLimits) {
      this.minCashThreshold = this.strategyLimits.minCashRebalancedMonthlyThreshold * 100;
    } else {
      this.minCashThreshold = this.strategyLimits ? this.strategyLimits?.minCashThreshold * 100 : 0;
    }
  }

  private getModelStrategyData(id: string): void {
    this.restApiService.getModelStrategy(id).subscribe({
      error: this.handleModelStrategyError.bind(this),
      next: this.handleModelStrategyData.bind(this),
    });
  }

  private handleModelStrategyError(error: unknown): void {
    if(isInputError(error, 404)) {
      this.router.navigate(['/strategy/new']);
    } else {
      this.loggerService.error(typeof error === 'function' ? error() : error);
      this.toastService.unknownApiError();
    }
    this.preloaders.modelStrategy = false;
  }

  private handleModelStrategyData(data: ModelStrategyOrPortfolioInstrumentOut): void {
    this.preloaders.modelStrategy = false;
    this.modelStrategyApiData = data;
    for (const instrument of this.modelStrategyApiData!.instruments) {
      const instrumentWeightInpercent = parseInt((instrument.weight * 100).toString());
      // Add instrument only if its instrument category is allowed
      if (this.loggedUser.instrumentCategories.some(ic => instrument.instrument.instrumentCategory === ic)) {
        this.instrumentsForm!.push(
          this.createInstrumentControl(
            instrument.instrument, instrumentWeightInpercent)
        );
      }

    }
    this.instrumentsService.selectedInstruments = this.instrumentsForm!;
    this.updateCashLimit();
    this.rebalancingSwitchDisabled = this.isInstrumentWithRebalancedMonthlySelected;
    this.initFormSub();
  }

  private createInstrumentControl(instrument: InstrumentDetailFullOut, initialValue = 0): AbstractControl {
    if (instrument.cash) {
      return this.fb.group({
        id: instrument.instrumentId,
        value: initialValue,
        isin: instrument.isin,
        instrumentCategory: instrument.instrumentCategory,
        cash: true,
      });
    } else {
      return this.fb.group({
        id: instrument.instrumentId,
        value: initialValue,
        name: instrument.name,
        humanName: instrument.humanName,
        isin: instrument.isin,
        instrumentCategory: instrument.instrumentCategory,
        cash: false,
        regionalOrientations: [instrument.regionalOrientations],
        rebalancedMonthly: instrument.rebalancedMonthly,
      });
    }
  }

  public getInstrumentControl(instrumentId: number): AbstractControl | null {
    if (this.instrumentsForm) {
      for (const instrumentControl of this.instrumentsForm.controls) {
        if (instrumentControl.value['id'] === instrumentId) {
          return instrumentControl;
        }
      }
    }

    return null;
    // todo add log?
    // throw new Error('Instrument not found!');
  }

  private validateEditStrategyForm(): boolean {
    let valid = true;
    if(this.modelChartForm.value['name']?.length < newPortfolioConfig.investmentNameMinLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.inputNameTooShort',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    const cashValue = this.cashControl()?.value ?? NaN;

    if(cashValue && cashValue.value < this.minCashThreshold) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.cashWeightIncorrect',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['name']?.length > newPortfolioConfig.investmentNameMaxLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.inputNameTooLong',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if (this.actualInstrumentsSum !== 100) {
      this.checksumValidMessages['checksumNot100'] = true;
      this.scrollToChecksum();
      valid = false;
    }

    if (this.instrumentsForm && this.instrumentsForm.controls.some(instrumentControl => instrumentControl.value.value === 0)) {
      this.checksumValidMessages['instrumentsWith0'] = true;
      this.scrollToChecksum();
      valid = false;
    }

    return valid;
  }

  private validateNewStrategyForm(): boolean {
    let valid = true;

    if(this.modelChartForm.value['name']?.length < newPortfolioConfig.investmentNameMinLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.inputNameTooShort',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['name']?.length > newPortfolioConfig.investmentNameMaxLength) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.inputNameTooLong',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if((this.modelChartForm.value['oneTimeDeposit'] + this.modelChartForm.value['regularDeposit']) < 0) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.negativeDeposit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.bothInvestmentsZero) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.bothInvestmentsZero',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['oneTimeDeposit'] > this.configService.getConfig(EnvironmentKeys.maximalInitialInvest)) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.initialDepositOverLimit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['oneTimeDeposit'] !== 0 && this.modelChartForm.value['oneTimeDeposit'] < this.configService.getConfig(EnvironmentKeys.minimalInvest)) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.initialDepositUnderLimit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['regularDeposit'] !== 0 && this.modelChartForm.value['regularDeposit'] < this.configService.getConfig(EnvironmentKeys.minimalInvest)) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.regularDepositUnderLimit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    if(this.modelChartForm.value['regularDeposit'] > this.configService.getConfig(EnvironmentKeys.maximalRegularInvest)) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.regularDepositOverLimit',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;
    }

    const selectedWeightsSum = getInstrumentsWeightSum(this.instrumentsForm!.value as FormInstrument[]);
    if(selectedWeightsSum !== 1) {
      // 1 = 100%
      this.toastService.addNotification({
        message: 'toastNotifications.messages.invalidInstrumentsSum',
        type: 'warning',
        component: ToastNotificationComponent
      });
      valid = false;

      // used to debugg BI-288
      if(selectedWeightsSum > 0.98 && selectedWeightsSum < 1.02 && this.instrumentsForm) {
        for (const instrument of this.instrumentsForm.value) {
          Sentry.addBreadcrumb({ data: {instrument: instrument} });
        }
        Sentry.captureException('Debugg message: Sum of selected weights might be in unexpected range, validated:' + selectedWeightsSum);
      }
    }

    return valid;
  }

  private initDipConfirmForm(): void {
    this.dipConfirmForm = new UntypedFormGroup({
      dip: new UntypedFormControl(false),
    });

    this.dipConfirmFormSubscription = this.dipConfirmForm.valueChanges.pipe(
      distinctUntilChanged(
        (prev, curr) => prev.dip === curr.dip)
      ).subscribe((changes) => {
        if(changes.dip) {
          this.createStrategy(true);
        }
      }
    );
  }

  public createStrategy(dipConfirmed?: true): void {
    if(!dipConfirmed && this.modelChartForm.value.dip.dip) {
      // secondary config if DIP is selected before posting
      this.initDipConfirmForm();
      const dipControl = this.dipConfirmForm.controls['dip'];

      if(dipControl.value === false) {
        this.modalService.openDipModal(dipControl, 'new-strategy');
      }
      return;
    }

    this.modelChartForm.markAllAsTouched();
    this.modelChartForm.updateValueAndValidity();

    if(!this.validateNewStrategyForm()) {
      return;
    }

    const instruments: Record<string, number> = {};

    for (const instrument of (this.instrumentsForm!.value as FormInstrument[])) {
      if (instrument.value > 0) {
        instruments[instrument.isin] = parseFloat((instrument.value / 100).toFixed(2).toString()); // 1.0 = 100%
      }
    }

    const mappedStrategyData: ApiExpectedStrategy = {
      name: this.modelChartForm.value.name,
      currencyCode: this.loggedUser.currencyCode,
      instruments,
      investmentFrequency: 'ONE_TIME',
      initialDeposit: this.modelChartForm.value.oneTimeDeposit,
      regularDeposit: this.modelChartForm.value.regularDeposit,
      weeksBetweenInvestments: this.modelChartForm.value.depositRegularity.value,
      totalYearsToInvest: this.modelChartForm.value.timeOfInvestment,
      rebalance: this.modelChartForm.value.rebalancing,
      frameworkContractId: this.modelChartForm.value.dip.frameworkContractId,
    }

    if(mappedStrategyData.regularDeposit >= this.configService.getConfig(EnvironmentKeys.limitForInvestmentCreateConfirm)) {
      // timeout used to peacefully close previous confirm modal todo - some better approach for modals chaining
      setTimeout(() => {
        this.modalService.openNewInvestmentConfirm(mappedStrategyData);
        this.creationConfirmSubscription = this.signalsService.confirmNewStrategyHighRegular$.subscribe(updatedStrategy => {
          if(updatedStrategy) {
            this.postNewStrategy(updatedStrategy);
          }

          if (this.creationConfirmSubscription) {
            this.creationConfirmSubscription.unsubscribe();
          }
        });
      }, 15);
    } else {
      this.postNewStrategy(mappedStrategyData);
    }
  }

  private postNewStrategy(updatedStrategy: ApiExpectedStrategy) {
    this.preloaders.postingStrategy = true;
    this.restApiService.postStrategy(updatedStrategy).subscribe({
      next: this.strategyCreated.bind(this),
      error: this.handleCreationError.bind(this),
    });

    if(this.creationConfirmSubscription) {
      this.creationConfirmSubscription.unsubscribe();
    }
  }

  private scrollToChecksum() {
    this.strategyChecksum.nativeElement
    .scrollIntoView({ behavior: 'smooth' });
  }

  public updateStrategy(): void {
    if(!this.editId || !this.validateEditStrategyForm()) {
      return;
    }

    const instruments: Record<string, number> = {};

    // rebalancing confirm modal
    // when editing strategy with active rebalancing, adding instrument with rebalancedMonthly = true  should disable rebalancing in investment
    if(this.isInstrumentWithRebalancedMonthlySelected && this.modelChartForm.value.rebalancing) {
      this.modalService.openRebalancingSwitchoffConfirm();

      this.signalsService.rebalancingSwitchConfirmed$.pipe(
        take(1),
        takeUntil(this.signalsService.modalClosed$)
      )
      .subscribe(() => {
        this.modelChartForm.patchValue({ rebalancing: false })
        this.updateStrategy();
      });

      return;
    }

    for (const instrument of (this.instrumentsForm!.value as FormInstrument[])) {
      if (instrument.value > 0) {
        instruments[instrument.isin] = parseFloat((instrument.value / 100).toFixed(2).toString()); // 1.0 = 100%
      }
    }

    // fees change modal
    // portfolioChangeAffectsFees - api based flag if editing can change fees
    // only display if changed weights or instruments and not already confirmed
    if(
      this.strategyOriginalValues.portfolioChangeAffectsFees
      && !this.confirmedFeesChange
      && this.instrumentsEdited()
    ) {
      this.modalService.openFeeChangeConfirm();

      this.signalsService.feesChangeConfirmed$.pipe(
        take(1),
        takeUntil(this.signalsService.modalClosed$)
      )
      .subscribe(() => {
        this.confirmedFeesChange = true;
        this.updateStrategy();
      });

      return;
    }

    let input: ApiUpdateStrategy | ApiEfektikaUpdateStrategy = {
      instruments,
      rebalance: this.modelChartForm.value.rebalancing,
      newPortfolioName: this.modelChartForm.value['name'],
      initialDeposit: this.modelChartForm.value['oneTimeDeposit'],
      regularDeposit: this.modelChartForm.value['regularDeposit'],
      weeksBetweenInvestments: this.getWeeksBetweenInvestmentsEnumByNumber(+this.modelChartForm.value['depositRegularity'].value as 1 | 4 | 12),
      totalYearsToInvest: this.modelChartForm.value['timeOfInvestment']
    } as ApiUpdateStrategy
    let request: Observable<void> = this.restApiService.updateStrategy(input, this.editId);

    if (this.editingEfektika) {
      input = {
        newEfektikaName: this.modelChartForm.value['name'],
        rebalance: this.modelChartForm.value.rebalancing,
      } as ApiEfektikaUpdateStrategy;
      request = this.restApiService.updateEfektikaStrategy(input, this.editId);
    }

    this.preloaders.editStrategy = true;
    request.subscribe({
      next: this.strategyUpdated.bind(this),
      error: this.strategyUpdateError.bind(this),
    });
  }

  private getWeeksBetweenInvestmentsEnumByNumber(wbi: 1 | 4 | 12): InvestmentFrequency {
    const map: Record<1 | 4 | 12, InvestmentFrequency> = {
      1: InvestmentFrequency.WEEKLY,
      4: InvestmentFrequency.MONTHLY,
      12: InvestmentFrequency.QUARTERLY,
    };
    return map[wbi];
  }

  private instrumentsEdited(): boolean {
    if(!this.instrumentsForm || this.instrumentsForm!.value.length !== this.strategyOriginalValues.instrumentsWithWeights.length) {
      return true;
    }

    for (const originalInstrument of this.strategyOriginalValues.instrumentsWithWeights) {
      const updatedInstrument = (this.instrumentsForm!.value as FormInstrument[]).find(instrument => instrument.id === originalInstrument.instrument.instrumentId);
      if(!updatedInstrument || (parseFloat((updatedInstrument.value / 100).toString()) !== originalInstrument.weight)) {
        return true;
      }
    }

    return false;
  }

  private strategyUpdated(): void {
    this.preloaders.editStrategy = false;
    this.toastService.addNotification({
      title: 'toastNotifications.headers.success',
      message: 'toastNotifications.messages.strategyUpdated',
      type: 'success',
      component: ToastNotificationComponent
    });
    this.router.navigate(['/investment-detail/' + this.editId]);
  }

  private strategyUpdateError(error: unknown): void {
    this.preloaders.editStrategy = false;
    if(investmentAlreadyExistError(error)) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.strategyNameAlreadyExist',
        type: 'warning',
        component: ToastNotificationComponent
      });
    } else {
      this.loggerService.error(typeof error === 'function' ? error() : error);
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.portfolioUpdateFailed',
        type: 'warning',
        component: ToastNotificationComponent
      });
    }
  }

  private handleCreationError(error: unknown): void {
    this.preloaders.postingStrategy = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);

    if(investmentAlreadyExistError(error)) {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.strategyNameAlreadyExist',
        type: 'warning',
        component: ToastNotificationComponent
      });
    } else {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.error',
        message: 'toastNotifications.messages.strategyCreateFailed',
        type: 'warning',
        component: ToastNotificationComponent
      });
    }

    if(this.dipConfirmFormSubscription) {
      this.dipConfirmFormSubscription.unsubscribe();
    }
  }

  private strategyCreated(response: unknown): void {
    if (!(
      typeof response === 'object' &&
      response !== null &&
      'portfolioId' in response
    )) {
      this.handleCreationError('Unknown server response for create Strategy.');
    } else {
      this.toastService.addNotification({
        title: 'toastNotifications.headers.success',
        message: 'toastNotifications.messages.strategyCreated',
        type: 'success',
        component: ToastNotificationComponent
      });

      this.router.navigate(['/investment-detail', response['portfolioId']]);
    }

    if(this.dipConfirmFormSubscription) {
      this.dipConfirmFormSubscription.unsubscribe();
    }
    if(this.creationConfirmSubscription) {
      this.creationConfirmSubscription.unsubscribe();
    }
  }

  public cashControl(): AbstractControl | null {
    const cashControl = this.instrumentsForm?.controls.find(control => control.value['cash'] === true);

    return cashControl ?? null;
  }

  public cashInstrumentDetail(): InstrumentDetailFullOut {
    const cashKey = this.cashControl()?.value['isin'];
    const cashInstrument = this.availableCash?.find(cash => cash.isin === cashKey);

    return {
      cashInstrument
    } as unknown as InstrumentDetailFullOut;
  }

  public getCurrencyFromCode(currencyCode: keyof typeof CurrencyCodesEnum): AvailableCurrencySymbols {
    return getCurrencyFromCode(currencyCode);
  }

  public rebalancingSwitched(): void {
    const rebalancingControl = this.modelChartForm.controls['rebalancing'];

    if(rebalancingControl.value === false) {
      this.modalService.openRebalancingModal(rebalancingControl);
    } else {
      rebalancingControl.patchValue(false);
    }
  }

  // If instrument not present in allowed categories (but was there before),
  // user may adjust this instrument's weight, but not set it greater than the current value
  // This also applies to instruments that are marked for removal but are present in the strategy already
  public getInstrumentMax(instrument: InstrumentDetailFullOut): number {
    if(this.editingEfektika) {
      return EfektikaInstrumentWeights.singleEfektikaInstrumentWeight;
    }
    if (this.editId && this.strategyOriginalValues.instrumentsWithWeights?.length) {
      const weight = this.strategyOriginalValues.instrumentsWithWeights.find(iww => iww.instrument.instrumentId === instrument.instrumentId)?.weight;
      const instrumentStillAllowed = this.loggedUser.instrumentCategories.some(ic => ic === instrument.instrumentCategory);
      if (weight && (!instrumentStillAllowed || instrument.willBeRemoved)) {
        return weight * 100;
      }
    }
    return 100;
  }

  private initRebalancingTooltip(): void {
    if(this.tooltipDisabledRebalancingByEstateContent && this.tooltipDisabledRebalancingByEstateHoverable) {
      tippy(this.tooltipDisabledRebalancingByEstateHoverable.nativeElement, {
        content: this.tooltipDisabledRebalancingByEstateContent.nativeElement.innerHTML,
        allowHTML: true,
        placement: 'auto',
      });
    }
  }

  public discardChanges(): void {
    this.location.back();
  }

  public loadInstrumentsEditForPMWithStrategies(): void {
    if (this.loggedUser?.managementType === ManagementTypesEnum.PORTFOLIO_MANAGEMENT)
    this.loadingForInstrumentsEdit = true;
  }

  private initFormSub(): void {
    // OBS for invest params
    const form$ = this.modelChartForm.valueChanges.pipe(
      debounceTime(70),
      distinctUntilChanged((prev, curr) =>
        prev.oneTimeDeposit === curr.oneTimeDeposit &&
        prev.regularDeposit === curr.regularDeposit &&
        prev.timeOfInvestment === curr.timeOfInvestment &&
        prev.riskProfile === curr.riskProfile &&
        prev.depositRegularity === curr.depositRegularity &&
        prev.dip === curr.dip
      ),
    );

    // OBS for instruments and weight changes
    const instruments$ = this.instrumentsForm!.valueChanges.pipe(
      map((instruments: FormInstrument[]) =>
        instruments.map((instrument) => ({
          isin: instrument.isin,
          value: +instrument.value,
        })),
      ),
    );

    // combine them to trigger fee-calc for changes on either of them
    this.formSubscription = combineLatest([form$, instruments$])
      .pipe(distinctUntilChanged(isEqual))
      .subscribe(
        ([changes, instruments]: [ClientPortfolioEntryFeeCalcInput, FormInstrument[]]) => {
          const isinToWeightMap = new Map<string, number>();

          instruments.forEach(instrument => {
            if (instrument.isin && instrument.value > 0) {
              isinToWeightMap.set(instrument.isin, parseFloat((instrument.value / 100).toString()));
            }
          });
          this.entryFeeCalcService.calculateEntryFeeToDisplay({
            changes: {
              ...changes,
              riskProfile: 0, // strategy = risk score 0
              instrumentsWeights: isinToWeightMap
            },
          }, this.editId ?? null)

          this.updateDevelopmentChart(changes, isinToWeightMap);
      });
  }

  private updateDevelopmentChart(changes: ClientPortfolioEntryFeeCalcInput, isinToWeightMap: Map<string, number>): void {
    if(isNaN(changes.regularDeposit) || isNaN(changes.oneTimeDeposit) || isNaN(changes.timeOfInvestment)) {
      return;
    }
    const selectedWeightsSum = getInstrumentsWeightSum(this.instrumentsForm!.value as FormInstrument[]);
    if(selectedWeightsSum === 1) {
      this.preloaders.modelChart = true;
      this.apiErrors.insufficentModelChartWeights = false;
      this.restApiService.getStrategyModelChart({
        isinToWeightMap: Object.fromEntries(isinToWeightMap),
        weeksBetweenInvestments: parseInt(changes.depositRegularity.value),
        yearsToInvest: changes.timeOfInvestment,
        initialDeposit: changes.oneTimeDeposit,
        regularDeposit: changes.regularDeposit,
      }).subscribe({
        next: (data) => this.modelChartUpdated(data),
        error: (error) => this.modelChartUpdateError(error),
      });
    } else {
      this.apiErrors.insufficentModelChartWeights = true;
    }
  }

  private modelChartUpdated(data: ApiModelPortfolioChartOut): void {
    this.apiErrors.modelChart = false;
    this.preloaders.modelChart = false;
    data = parseTimestamps(data);
    this.replaceDevelopmentChartData(data);
    this.updateDevelopmentChartLabels(data);
  }

  private modelChartUpdateError(error: unknown): void {
    this.preloaders.modelChart = false;
    this.apiErrors.modelChart = true;
    this.loggerService.error(error);
    this.toastService.addNotification({
      message: 'toastNotifications.messages.developmentChartUpdateError',
      type: 'warning',
      component: ToastNotificationComponent
    });
  }

  private replaceDevelopmentChartData(data: ApiModelPortfolioChartOut): void {
    this.emptyDevelopmentChart();
    this.developmentChartData['optimisticScenario'].push(...data.optimistic);
    this.developmentChartData['probableScenario'].push(...data.expected);
    this.developmentChartData['pessimisticScenario'].push(...data.pessimistic);
    this.developmentChartData['deposits'].push(...data.pureInvestment);
  }

  private emptyDevelopmentChart(): void {
    this.developmentChartData['optimisticScenario'].length = 0;
    this.developmentChartData['probableScenario'].length = 0;
    this.developmentChartData['pessimisticScenario'].length = 0;
    this.developmentChartData['deposits'].length = 0;
  }

  private updateDevelopmentChartLabels(data: ApiModelPortfolioChartOut) {
    const dates = data.pureInvestment.map(date => date.x);
    this.developmentChartLabels.length = 0;
    this.developmentChartLabels.push(...adjustDevelopmentTimeLabels(dates));
  }

  public ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
    this.entryFeeToDisplaySub?.unsubscribe();
  }
}
